/**
 * Snapchat Advertising Error Parser (Story 19.7, Task 2)
 *
 * Maps Snap Marketing API v1 error responses to standardized error types.
 *
 * Snap surfaces errors in a REST envelope:
 *   {
 *     "request_status": "ERROR",
 *     "request_id": "...",
 *     "debug_message": "...",
 *     "display_message": "...",
 *     "error_code": "..."
 *   }
 *
 * The parser inspects HTTP status first (the cheapest signal) and only falls
 * back to body-shape sniffing when the status isn't in our table.
 *
 * `display_message` is extracted for user-facing copy. `debug_message` is
 * sanitized and logged only — never surfaced to users (may contain internal
 * IDs).
 */

// ============================================================================
// Types
// ============================================================================

export type SnapchatAdsErrorType =
  | "token_expired"
  | "permission_denied"
  | "not_found"
  | "rate_limited"
  | "provider_unavailable"
  | "unknown";

export interface SnapchatAdsApiError {
  type: SnapchatAdsErrorType;
  message: string;
  status: number | null;
  retryable: boolean;
}

interface RestErrorEnvelope {
  request_status?: string;
  request_id?: string;
  /** Internal debug message — sanitized for logs, never surfaced to users */
  debug_message?: string;
  /** User-facing message — safe to display */
  display_message?: string;
  /** Snap-specific error code */
  error_code?: string;
  /** Standard OAuth 2.0 error fallback (rare on data endpoints) */
  error?: string;
  error_description?: string;
}

// ============================================================================
// Status Code Mapping
// ============================================================================

const STATUS_CODE_MAP: Record<
  number,
  { type: SnapchatAdsErrorType; retryable: boolean }
> = {
  401: { type: "token_expired", retryable: false },
  403: { type: "permission_denied", retryable: false },
  404: { type: "not_found", retryable: false },
  429: { type: "rate_limited", retryable: true },
  500: { type: "provider_unavailable", retryable: true },
  502: { type: "provider_unavailable", retryable: true },
  503: { type: "provider_unavailable", retryable: true },
  504: { type: "provider_unavailable", retryable: true },
};

// ============================================================================
// Error Parser
// ============================================================================

/**
 * Parses a Snap Ads error response into a standardized error object.
 *
 * Resolution order:
 *   1. HTTP status (fastest, most reliable)
 *   2. REST envelope `display_message` (or `error_description` for OAuth-style errors)
 *   3. Fallback to "unknown"
 *
 * @returns The mapped error, or null when the input is empty/successful
 *          (HTTP 2xx with no error body).
 */
export function parseSnapchatAdsApiError(
  responseBody: unknown,
  httpStatus?: number
): SnapchatAdsApiError | null {
  const body = (responseBody ?? {}) as RestErrorEnvelope;

  // ---- 1. HTTP status mapping ------------------------------------------------
  if (httpStatus && (httpStatus < 200 || httpStatus >= 300)) {
    const mapping = STATUS_CODE_MAP[httpStatus];
    if (mapping) {
      const message = extractErrorMessage(body, httpStatus);
      return {
        type: mapping.type,
        message,
        status: httpStatus,
        retryable: mapping.retryable,
      };
    }
  }

  // ---- 2. REST envelope without explicit code mapping ------------------------
  if (body.display_message) {
    return {
      type: "unknown",
      message: body.display_message,
      status: httpStatus ?? null,
      retryable: false,
    };
  }
  if (body.error_description) {
    return {
      type: "unknown",
      message: body.error_description,
      status: httpStatus ?? null,
      retryable: false,
    };
  }

  // ---- 3. No identifiable error -----------------------------------------------
  if (!httpStatus || (httpStatus >= 200 && httpStatus < 300)) {
    return null;
  }

  return {
    type: "unknown",
    message: extractErrorMessage(body, httpStatus),
    status: httpStatus,
    retryable: false,
  };
}

// ============================================================================
// Typed error class
// ============================================================================

/**
 * Thrown by Snap REST helpers when Snap returns a non-2xx response. Carries
 * the parsed error type so the adapter can route it to the correct
 * `AdapterErrorCode` without string-matching the message.
 */
export class SnapchatAdsApiCallError extends Error {
  readonly type: SnapchatAdsErrorType;
  readonly status: number | null;
  readonly retryable: boolean;
  constructor(parsed: SnapchatAdsApiError, prefix: string) {
    super(`${prefix}: ${parsed.message}`);
    this.name = "SnapchatAdsApiCallError";
    this.type = parsed.type;
    this.status = parsed.status;
    this.retryable = parsed.retryable;
  }
}

/**
 * Maps a Snap Ads error type to the corresponding `AdapterErrorCode`.
 * Used by the adapter so we don't string-match localised provider messages.
 *
 * Note: 404 → `invalid_response` (NOT `account_not_found`) because Snap 404
 * during onboarding means "endpoint URL changed" or "schema drift" — the
 * benign onboarding case (zero ad accounts) is checked explicitly by the
 * caller before throwing.
 */
export function snapchatAdsErrorTypeToAdapterCode(
  type: SnapchatAdsErrorType
):
  | "token_expired"
  | "permission_denied"
  | "rate_limited"
  | "provider_unavailable"
  | "invalid_response"
  | "account_not_found" {
  switch (type) {
    case "token_expired":
      return "token_expired";
    case "permission_denied":
      return "permission_denied";
    case "not_found":
      return "invalid_response";
    case "rate_limited":
      return "rate_limited";
    case "provider_unavailable":
      return "provider_unavailable";
    case "unknown":
    default:
      return "invalid_response";
  }
}

/**
 * Pulls the most descriptive message we can find out of a Snap error envelope,
 * with a sensible fallback when nothing structured is present.
 *
 * Prefers `display_message` (user-safe) over `debug_message` (may contain
 * internal IDs).
 */
function extractErrorMessage(body: RestErrorEnvelope, status: number): string {
  if (body.display_message) return body.display_message;
  if (body.error_description) return body.error_description;
  if (body.error) return body.error;
  if (body.error_code) return `Snapchat Ads error: ${body.error_code}`;
  return `Snapchat Ads API error: HTTP ${status}`;
}
